<template>
	<div ref="dropdownContainer">
		<div ref="referenceEl" :class="dropdownClasses" @click="toggleDropdown">
			<template v-if="variant === DropdownVariant.DEFAULT">
				<div>
					<div class="base-dropdown__label">{{ label }}</div>
					<div v-if="modelValue" class="base-dropdown__selected-option">
						{{ modelValue.label }}
					</div>
					<div
						v-if="isDropdownOpen && !modelValue"
						class="base-dropdown__placeholder"
					>
						{{ placeholder }}
					</div>
				</div>
				<div :class="[isDropdownOpen ? '-rotate-90' : 'rotate-90']">
					<BaseIcon
						name="chevron_thin"
						class="h-24px w-24px flex-shrink-0 rounded-[50%]"
						:class="{ 'fill-n-300': isDisabled }"
					/>
				</div>
			</template>

			<template v-if="variant === DropdownVariant.STANDARD">
				<div v-if="!modelValue" class="base-dropdown-standard__main">
					<slot name="standard-icon"></slot>
					<div class="base-dropdown-standard__label">{{ label }}</div>
				</div>
				<div v-else class="base-dropdown-standard__selected-option">
					{{ modelValue.label }}
				</div>
				<div :class="[isDropdownOpen ? '-rotate-90' : 'rotate-90']">
					<BaseIcon
						name="chevron_thin"
						class="h-24px w-24px flex-shrink-0 rounded-[50%]"
						:class="{ 'fill-n-300': isDisabled }"
					/>
				</div>
			</template>
		</div>

		<div v-if="!isError" class="base-dropdown__helper">
			<slot name="helper-message">
				<template v-if="helperMessage">
					<BaseIcon
						v-if="isShowHelperIcon"
						name="check_circle"
						class="h-16px w-16px flex-shrink-0 fill-n-500"
					/>
					<div class="base-dropdown__helper-text">{{ helperMessage }}</div>
				</template>
			</slot>
		</div>

		<div v-else class="base-dropdown__error">
			<slot name="error-message">
				<template v-if="errorMessage">
					<BaseIcon
						v-if="isShowHelperIcon"
						name="warning_circle"
						class="h-16px w-16px flex-shrink-0 fill-r-700"
					/>
					<div class="base-dropdown__error-text">{{ errorMessage }}</div>
				</template>
			</slot>
		</div>

		<div
			v-show="isDropdownOpen"
			ref="floatingEl"
			class="base-dropdown__menu"
			:style="{ '--max-display-items': maxDisplayItems }"
		>
			<div
				v-for="option in options"
				:key="option.value"
				class="base-dropdown__menu-item"
				:class="{
					active: modelValue ? option.value === modelValue.value : false,
				}"
				@click="selectOption(option)"
			>
				<BaseIcon
					name="checkmark"
					class="h-14px w-14px flex-shrink-0 fill-o-500 opacity-0"
					:class="[
						(modelValue ? option.value === modelValue.value : false)
							? 'opacity-100'
							: 'opacity-0',
					]"
				/>
				<div class="base-dropdown__menu-item-text">{{ option.label }}</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
/* eslint-disable import/first */
// Prevent eslint moving the enum and interface back to script setup section which causes error
import { onMounted, ref, onBeforeUnmount, watch, computed } from 'vue'
import { computePosition, flip, autoUpdate, offset } from '@floating-ui/dom'
import { onClickOutside } from '@vueuse/core'

export interface DropdownOption {
	label: string
	value: string | number
}

export enum DropdownVariant {
	DEFAULT = 'DEFAULT',
	STANDARD = 'STANDARD',
}
</script>

<script lang="ts" setup>
import type { PropType } from 'vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const props = defineProps({
	modelValue: {
		type: Object as PropType<DropdownOption | null>,
		required: true,
		default: null,
	},
	label: {
		type: String,
		required: true,
	},
	options: {
		type: Array as PropType<DropdownOption[]>,
		required: true,
	},
	placeholder: {
		type: String,
		default: '',
	},
	isShowHelperIcon: {
		type: Boolean,
		default: true,
	},
	helperMessage: {
		type: String,
		default: '',
	},
	isError: {
		type: Boolean,
		default: false,
	},
	isShowErrorIcon: {
		type: Boolean,
		default: true,
	},
	errorMessage: {
		type: String,
		default: '',
	},
	isDisabled: {
		type: Boolean,
		default: false,
	},
	variant: {
		type: String as PropType<DropdownVariant>,
		default: DropdownVariant.DEFAULT,
		validator: (value: string) =>
			Object.values(DropdownVariant).includes(value as DropdownVariant),
	},
	maxDisplayItems: {
		// The max number of items to display in the dropdown menu, show scrollbar if the number of items is greater than this value
		type: Number,
		default: 3,
	},
})

const emit = defineEmits([
	'click',
	'blur-without-selection',
	'update:modelValue',
])

const dropdownContainer = ref<HTMLElement | null>(null)
const referenceEl = ref<HTMLElement | null>(null)
const floatingEl = ref<HTMLElement | null>(null)
const isDropdownOpen = ref(false)
const hasBeenOpened = ref(false)
let cleanupAutoUpdate: (() => void) | null = null

onClickOutside(dropdownContainer, () => {
	closeDropdown()
})

const dropdownClasses = computed(() => ({
	'base-dropdown': true,
	'base-dropdown-standard': props.variant === DropdownVariant.STANDARD,
	opened: isDropdownOpen.value,
	hasSelected: props.modelValue,
	error: props.isError,
	disabled: props.isDisabled,
}))

const toggleDropdown = () => {
	isDropdownOpen.value = !isDropdownOpen.value
	if (isDropdownOpen.value) {
		hasBeenOpened.value = true
	}
	emit('click')
}

const closeDropdown = () => {
	if (isDropdownOpen.value && hasBeenOpened.value && !props.modelValue) {
		emit('blur-without-selection')
	}
	isDropdownOpen.value = false
}

const selectOption = (option: DropdownOption) => {
	emit('update:modelValue', option)
	hasBeenOpened.value = false
	toggleDropdown()
}

const handleScrollbarPresence = () => {
	if (!floatingEl.value) return

	// Make sure the right padding is reduced when the scrollbar appears, otherwise there will be extra padding
	const menuHasScrollbar =
		floatingEl.value.scrollHeight > floatingEl.value.clientHeight
	floatingEl.value.classList.toggle('has-scrollbar', menuHasScrollbar)
}

watch(
	() => isDropdownOpen.value,
	newValue => {
		if (newValue) {
			handleScrollbarPresence()
		}
	},
)

const updatePosition = () => {
	if (!referenceEl.value || !floatingEl.value) return
	const referenceWidth = referenceEl.value.offsetWidth

	computePosition(referenceEl.value, floatingEl.value, {
		placement: 'bottom',
		middleware: [flip(), offset(4)],
	}).then(({ x, y }: { x: number; y: number }) => {
		if (!floatingEl.value) return

		Object.assign(floatingEl.value.style, {
			left: `${x}px`,
			top: `${y}px`,
			width: `${referenceWidth}px`,
		})
	})
}

const initializePositioning = () => {
	if (!referenceEl.value || !floatingEl.value) return
	cleanupAutoUpdate = autoUpdate(
		referenceEl.value,
		floatingEl.value,
		updatePosition,
	)
}

onMounted(() => {
	initializePositioning()
})

onBeforeUnmount(() => {
	if (cleanupAutoUpdate) {
		cleanupAutoUpdate()
	}
})
</script>
